import { memo, useRef } from 'react';
import Modal from 'shared/Modal';
import { Box, Button, Flex, SxStyleProp } from 'theme-ui';
import useToggle from 'react-use/lib/useToggle';
import Icon, { IconTypes } from 'shared/Icon';
import { useTranslator } from '@picsart/rc/services/localization';

export const fullSize: SxStyleProp = {
  objectFit: 'contain',
  maxHeight: 400,
  maxWidth: 352,
  backgroundColor: 'white'
};

const previewStyles: SxStyleProp = {
  ...fullSize,
  bg: 'text',
  position: 'relative'
};

type TemplatePreviewModalContentProps = {
  previewUrl: string;
};

const TemplatePreviewModalContent = ({ previewUrl }: TemplatePreviewModalContentProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, toggleMuted] = useToggle(true);

  return (
    <div sx={previewStyles}>
      <video src={previewUrl} muted={muted} sx={fullSize} ref={videoRef} autoPlay={true} loop />
      <div
        onClick={() => toggleMuted()}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 32,
          height: 32,
          padding: '4px',
          boxSizing: 'border-box',
          bg: 'overlay',
          opacity: 0.8,
          borderRadius: 8
        }}
      >
        <Icon
          name={muted ? 'mute-editor' : 'unmute-editor'}
          type={IconTypes.White}
          sx={{ height: '100%', cursor: 'pointer' }}
        />
      </div>
    </div>
  );
};

type TemplatePreviewModalProps = {
  previewUrl: string;
  opened: boolean;
  closeHandler: () => void;
  applyHandler: () => void;
};

export const TemplatePreviewModal = memo(
  ({ previewUrl, opened, closeHandler, applyHandler }: TemplatePreviewModalProps) => {
    const translator = useTranslator();

    return (
      <Modal
        color="transparent"
        opened={opened}
        closeHandler={closeHandler}
        title={translator('Template')}
      >
        <Box sx={{ width: 352 }}>
          <Flex sx={{ flexDirection: 'column', alignItems: 'center' }}>
            <TemplatePreviewModalContent previewUrl={previewUrl} />
            <Button
              data-test="template-preview-modal_use"
              variant="primary"
              onClick={applyHandler}
              sx={{
                px: 7,
                py: 4,
                mt: 6,
                alignSelf: 'flex-end',
                fontSize: 2,
                borderStyle: 'solid',
                borderWidth: 1,
                borderColor: 'primary'
              }}
            >
              {translator('Use')}
            </Button>
          </Flex>
        </Box>
      </Modal>
    );
  }
);
